import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { useT } from '../i18n.jsx';

// Filter controls used by the Talent Bank report. Presentational and role-blind: each takes a set of
// options and a callback, and none of them knows whose data is being filtered.

export const ToggleGroup = ({ options, value, onChange }) => (
  <div style={{ display: 'flex', gap: '0.25rem' }}>
    {options.map(opt => (
      <button
        key={opt.value}
        onClick={() => onChange(opt.value)}
        className={value === opt.value ? 'btn-primary' : 'btn-outline'}
        style={{ padding: '0.35rem 0.9rem', fontSize: '0.8rem', height: 'auto', margin: 0, whiteSpace: 'nowrap' }}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

export const CheckboxGroup = ({ items, activeSet, labelFn, onToggle }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
    {items.map(item => (
      <label key={item} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={activeSet.has(item)}
          onChange={() => onToggle(item)}
          style={{ width: '14px', height: '14px', cursor: 'pointer' }}
        />
        {labelFn(item)}
      </label>
    ))}
  </div>
);

// Compact dropdown that opens a checkbox list, so a single control can hold a multi-select value.
// The panel is rendered through a portal so it isn't clipped by the scrollable tabs bar it lives in.
export const MultiSelectDropdown = ({ items, activeSet, labelFn, onToggle, placeholder, width }) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (
        wrapperRef.current && !wrapperRef.current.contains(e.target) &&
        (!panelRef.current || !panelRef.current.contains(e.target))
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const toggleOpen = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPanelStyle({ top: rect.bottom + 4, left: rect.left, minWidth: rect.width });
    }
    setOpen(o => !o);
  };

  const summary = activeSet.size === 0
    ? placeholder
    : activeSet.size === 1
      ? labelFn([...activeSet][0])
      : t('common.selectedCount', { count: activeSet.size });

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width, flexShrink: 0 }}>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleOpen}
        className="input-field"
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.4rem', cursor: 'pointer', textAlign: 'left' }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        <ChevronDown size={14} style={{ flexShrink: 0, color: 'var(--color-text-muted)' }} />
      </button>
      {open && panelStyle && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed', top: panelStyle.top, left: panelStyle.left, minWidth: panelStyle.minWidth,
            backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)', zIndex: 9999, padding: '0.5rem', display: 'flex', flexDirection: 'column',
            gap: '0.4rem', maxHeight: '260px', overflowY: 'auto',
          }}
        >
          {items.map(item => (
            <label key={item} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={activeSet.has(item)}
                onChange={() => onToggle(item)}
                style={{ width: '14px', height: '14px', cursor: 'pointer' }}
              />
              {labelFn(item)}
            </label>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};
